/**
 * O transporte: um `<iframe>` oculto com documento próprio — F124.
 *
 * ## Por que não `window.print()` na própria página
 *
 * `apps/web/src/app.css` imprime hoje escondendo o corpo inteiro e promovendo
 * `#aeris-receipt`, sob `@page { margin: 4mm }`. O recibo e o orçamento
 * dependem disso.
 *
 * Um adesivo precisa de `@page { size: 50mm 30mm; margin: 0 }`, e **`@page` não
 * pode ser redefinido condicionalmente por seletor** — é uma regra de página,
 * não de elemento. Acomodar etiqueta naquela folha põe o recibo em risco a cada
 * ajuste de etiqueta, e o recibo é o que impede a loja de operar.
 *
 * Documento próprio num iframe de mesma origem resolve pela raiz: o `@page` de
 * lá é o de lá, o `app.css` nem é carregado, e o recibo não corre risco nenhum.
 *
 * Isso **não contraria** a seção 24 da Master Specification, pela razão que a
 * F027 já deixou escrita: ela proíbe depender de `window.print()` como solução
 * DESKTOP, e o mesmo `PrintDocument` já vira bytes ESC/POS para o shell e o
 * Edge escreverem direto. O documento não muda quando isso chegar; o transporte
 * muda.
 */

/** Quanto se espera pelo `load` antes de desistir e limpar o iframe. */
const LOAD_TIMEOUT_MS = 10_000

/**
 * O quadro da última impressão.
 *
 * Ele fica na página até a PRÓXIMA impressão, e não some um segundo depois. O
 * diálogo do Chrome (e o do WebView2 no desktop) monta a pré-visualização
 * depois que `print()` retorna; removido o quadro antes disso, a
 * pré-visualização sai em branco — foi o "a etiqueta de teste vem vazia".
 */
let lastFrame: HTMLIFrameElement | null = null

/**
 * Imprime um documento HTML completo.
 *
 * Devolve uma promessa que resolve quando o diálogo do navegador já foi aberto
 * — **não** quando o papel saiu. Ninguém sabe quando o papel saiu: o `afterprint`
 * dispara tanto para "imprimiu" quanto para "cancelou", e o diálogo do Chrome
 * nem é do navegador. Prometer mais que isso seria mentir para quem chamou.
 */
export const printHtmlDocument = async (html: string): Promise<void> => {
  lastFrame?.remove()

  const frame = document.createElement('iframe')
  lastFrame = frame
  /*
   * Fora da tela, e VISÍVEL. `display: none` não layouta, e `visibility:
   * hidden` o Chrome respeita também na impressão do quadro: os dois imprimem
   * papel em branco.
   */
  frame.setAttribute('aria-hidden', 'true')
  frame.setAttribute('tabindex', '-1')
  frame.style.position = 'fixed'
  frame.style.right = '100%'
  frame.style.bottom = '100%'
  frame.style.width = '210mm'
  frame.style.height = '297mm'
  frame.style.border = '0'

  await new Promise<void>((resolve, reject) => {
    let settled = false

    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      frame.remove()
      reject(new Error('A impressão demorou demais para ficar pronta.'))
    }, LOAD_TIMEOUT_MS)

    frame.addEventListener('load', () => {
      if (settled) return
      const view = frame.contentWindow
      /*
       * Só o `load` do NOSSO documento conta.
       *
       * O Chromium (Chrome, Edge e o WebView2 do desktop) dispara um `load`
       * síncrono, com `about:blank` e corpo vazio, no instante em que um iframe
       * entra na página. Imprimir nesse `load` imprime a folha em branco — era a
       * etiqueta A4 vazia da loja, mesmo com a pré-visualização certa no
       * diálogo. O documento de verdade é o `about:srcdoc`.
       */
      if (view !== null && view.location.href !== 'about:srcdoc') return
      settled = true
      window.clearTimeout(timer)
      try {
        if (view === null) throw new Error('O quadro de impressão não abriu.')
        // `focus` antes de `print`: sem ele, alguns navegadores imprimem a
        // página de fora do iframe, que é exatamente o que não queremos.
        view.focus()
        view.print()
        resolve()
      } catch (cause) {
        reject(cause instanceof Error ? cause : new Error(String(cause)))
      }
    })

    /*
     * `srcdoc`, e não `document.write` nem um blob.
     *
     * `document.write` num iframe recém-criado corre contra o `about:blank` que
     * ele já está carregando, e o `load` dispara para o documento errado —
     * papel em branco. Um blob: URL seria outra origem, e o `contentWindow`
     * ficaria inacessível.
     *
     * E o `srcdoc` vai ANTES de o quadro entrar na página: com ele já posto, o
     * Chromium não carrega o `about:blank` inicial. O filtro do `load` acima
     * continua lá para o navegador que carregar mesmo assim.
     */
    frame.srcdoc = html
    document.body.append(frame)
  })
}
